import type { Methods } from "penpal";
import { WindowMessenger, connect as connectPenpal } from "penpal";
import type { ChannelFactory } from "./channels/channel-descriptor.js";

/**
 * Distributes over a union of ExtensionFactory types and builds a typed
 * record keyed by each factory's channel literal.
 *
 * Given T = [ExtensionFactory<"html", R1>, ExtensionFactory<"debug", R2>]:
 *   T[number] = ExtensionFactory<"html", R1> | ExtensionFactory<"debug", R2>
 *
 * For each F in the union the mapped type extracts:
 *   key   → the channel literal (e.g. "debug")
 *   value → { methods: TRemoteMethods; destroy: () => void }
 */
export type ExtractChannel<T extends ReadonlyArray<ChannelFactory>> = {
  [F in T[number] as F extends ChannelFactory<infer C, any>
    ? C
    : never]: F extends ChannelFactory<any, infer R>
    ? { methods: R; destroy: () => void }
    : never;
};

export type ChannelConnections<T extends ReadonlyArray<ChannelFactory<any, any>>> = Promise<{
  destroyAll: () => void;
  channels: ExtractChannel<T>;
}>

export async function connect<
  T extends ReadonlyArray<ChannelFactory<any, any>>,
>(
  target: Window,
  channels: T,
): ChannelConnections<T> {
  const connectionMap = await channels.reduce(
    async (connectionsPromise, channelFactory) => {
      const connections = await connectionsPromise;
      const messenger = new WindowMessenger({
        remoteWindow: target,
        allowedOrigins: ["*"],
      });
      const channel = channelFactory(messenger);
      const channelConnection = connectPenpal<Methods>(channel);
      const resolvedMethods = await channelConnection.promise;
      return {
        ...connections,
        [channel.channel]: {
          methods: resolvedMethods,
          destroy: channelConnection.destroy,
        },
      };
    },
    Promise.resolve({} as Record<string, { methods: Methods; destroy: () => void }>),
  );

  return {
    destroyAll: () => {
      for (const connection of Object.values(connectionMap)) {
        connection.destroy();
      }
    },
    channels: connectionMap as unknown as ExtractChannel<T>,
  };
}
